"use client";

import type { FC, ReactNode } from "react";
import {
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessage,
  useRuntimeState,
  useThread,
  useThreadRuntime,
} from "@assistant-ui/react";
import { StopIcon } from "@radix-ui/react-icons";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { ArrowDownIcon, SendHorizontalIcon } from "lucide-react";

import { cn } from "..";
import { TooltipIconButton } from "../assistant-ui/tooltip-icon-button";
import { CodemodAILogoLiquid } from "../logo";
import { Markdown } from "../prompt-kit/markdown";
import { ReasoningAssistantUICompat } from "../prompt-kit/reasoning";
import { ScrollBar } from "../scroll-area";
import { BorderTrail } from "./border-trail";
import { useThreadContext } from "./thread-context";
import { useThreadScroll } from "./use-thread-scroll";

export interface CodemodAITask {
  title: string;
  onClose?: () => void;
}

export const CodemodAIThread: FC<{
  children?: ReactNode;
  header?: ReactNode;
  composerClassName?: string;
  contentClassName?: string;
  hasShader?: boolean;
}> = ({ children, header, composerClassName, contentClassName, hasShader }) => {
  const { isAtBottom } = useThreadScroll();
  return (
    <ScrollAreaPrimitive.Root asChild>
      <ThreadPrimitive.Root className="bg-background box-border flex h-full flex-col overflow-hidden">
        <ScrollAreaPrimitive.Viewport className="thread-viewport" asChild>
          <ThreadPrimitive.Viewport className="flex h-full flex-col items-center overflow-y-scroll scroll-smooth bg-inherit">
            {hasShader && (
              <div className="gradient-planet pointer-events-none absolute inset-0 z-10 aspect-[0.5] shrink-0 -translate-y-1/3 rounded-[50%] opacity-5 blur-[90px] dark:opacity-10" />
            )}
            {header}
            <div
              className={cn(
                contentClassName,
                "relative z-10 mx-auto flex size-full max-w-5xl flex-col items-center",
              )}
            >
              <CodemodAIWelcome />

              <ThreadPrimitive.Messages
                components={{
                  UserMessage: CodemodAIUserMessage,
                  AssistantMessage: CodemodAIAssistantMessage,
                }}
              />
            </div>

            {children}

            <ThreadPrimitive.If empty={false}>
              <div className="min-h-1 grow" />
            </ThreadPrimitive.If>

            <div
              className={cn(
                "sticky bottom-0 z-10 mt-3 flex w-full max-w-5xl flex-col items-center justify-end rounded-t-lg bg-inherit px-4 pb-4",
                composerClassName,
              )}
            >
              <ThreadScrollToBottom />
              <CodemodAIComposer />
              <div
                className={cn(
                  "via-background/50 to-background absolute -top-8 right-4 left-4 z-0 h-8 bg-linear-to-b from-transparent opacity-100 transition-opacity",
                  isAtBottom && "opacity-0",
                )}
              />
            </div>
          </ThreadPrimitive.Viewport>
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
      </ThreadPrimitive.Root>
    </ScrollAreaPrimitive.Root>
  );
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="absolute -top-8 z-20 rounded-full disabled:invisible"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const CodemodAIWelcome: FC = () => {
  return (
    <ThreadPrimitive.Empty>
      <div className="flex max-h-full flex-1 flex-col items-center justify-center gap-2 overflow-y-auto px-3 py-3 text-center">
        <CodemodAILogoLiquid className="size-16" />
        <h3 className="text-lg font-bold">
          How can I help you with your codebase?
        </h3>
        <p className="text-sm opacity-50">
          Ask anything related to ast-grep for code structural search, lint,
          rewriting.
        </p>
      </div>
    </ThreadPrimitive.Empty>
  );
};

const CodemodAIComposer: FC = () => {
  const lastMessage = useRuntimeState(
    useThreadRuntime(),
    (state) => state.messages[state.messages.length - 1],
  );
  const { threadId } = useThreadContext();
  const thread = useThread();

  const isLastMessageRunningAndAi =
    lastMessage?.role === "assistant" &&
    lastMessage.status.type === "running" &&
    threadId;

  return (
    <div className="absolute inset-x-0 bottom-0 z-90 mx-auto flex w-full max-w-[39rem] flex-col items-center gap-1 sm:relative sm:inset-x-auto sm:bottom-auto sm:max-w-full sm:gap-5">
      <div className="relative flex w-full flex-1 flex-col-reverse items-center justify-between gap-0 p-2 sm:flex-col sm:gap-5 sm:p-0">
        <div className="relative bottom-0 z-10 mt-2 flex w-full flex-col items-center justify-center gap-2 text-base">
          <div className="relative flex w-full flex-row justify-center gap-2">
            <ComposerPrimitive.Root className="group bg-muted/80 ring-foreground/10 focus-within:ring-border hover:ring-border hover:focus-within:ring-border @container/input relative mb-2 w-full max-w-[39rem] overflow-hidden rounded-2xl px-3 pb-12 shadow ring-1 shadow-black/5 backdrop-blur-xl duration-100 ring-inset focus-within:ring-1">
              <div className="relative z-10">
                <ComposerPrimitive.Input
                  autoFocus
                  dir="auto"
                  placeholder="Write a message..."
                  className="text-foreground my-0 max-h-[200px] min-h-16 w-full resize-none border-none bg-transparent px-3 pt-5 align-bottom text-sm shadow-none focus:outline-none focus-visible:ring-0"
                  rows={1}
                />
              </div>
              <BorderTrail
                className="from-primary/0 via-primary to-primary/20 bg-gradient-to-l"
                size={100}
                isAnimating={thread.isRunning}
              />
              <div className="absolute inset-x-0 bottom-0 flex max-w-full gap-1.5 border-2 border-transparent p-2">
                <div className="ml-auto flex flex-row items-end gap-1">
                  {!isLastMessageRunningAndAi && (
                    <ComposerPrimitive.Send asChild>
                      <TooltipIconButton
                        tooltip="Send"
                        variant="default"
                        className="size-9 rounded-full p-0 transition-opacity ease-in"
                      >
                        <SendHorizontalIcon />
                      </TooltipIconButton>
                    </ComposerPrimitive.Send>
                  )}
                  {isLastMessageRunningAndAi && (
                    <ComposerPrimitive.Cancel asChild>
                      <TooltipIconButton
                        tooltip="Cancel"
                        variant="outline"
                        className="size-9 rounded-full p-0 transition-opacity ease-in"
                      >
                        <StopIcon />
                      </TooltipIconButton>
                    </ComposerPrimitive.Cancel>
                  )}
                </div>
              </div>
            </ComposerPrimitive.Root>
          </div>
        </div>
      </div>
    </div>
  );
};

const CodemodAIUserMessage: FC = () => {
  return (
    <MessagePrimitive.Root className="mb-4 flex w-full justify-end">
      <div className="bg-muted text-foreground max-w-[75%] rounded-2xl px-3 py-2 text-sm leading-relaxed shadow">
        <MessagePrimitive.Content />
      </div>
    </MessagePrimitive.Root>
  );
};

const CodemodAIAssistantMessage: FC = () => {
  const message = useMessage();

  const isRunning = message.status?.type === "running";
  const isLastMessagePartText =
    message.content[message.content.length - 1]?.type === "text";

  const loadingDots = (
    <div className="mt-3 flex items-center gap-1">
      <div className="bg-foreground size-1.5 animate-pulse rounded-full duration-500" />
      <div className="bg-foreground size-1.5 animate-pulse rounded-full delay-75 duration-500" />
      <div className="bg-foreground size-1.5 animate-pulse rounded-full delay-100 duration-500" />
    </div>
  );

  return (
    <MessagePrimitive.Root className="mb-4 flex w-full justify-start px-2">
      <div className="w-full text-sm leading-relaxed">
        <MessagePrimitive.Content
          components={{
            Text: (props) => <Markdown children={props.text} />,
            Reasoning: ReasoningAssistantUICompat,
          }}
        />

        {(isRunning && !isLastMessagePartText) || message.content.length === 0
          ? loadingDots
          : null}
        <MessagePrimitive.Error>
          <ErrorDisplay />
        </MessagePrimitive.Error>
      </div>
    </MessagePrimitive.Root>
  );
};

const ErrorDisplay = () => (
  <ErrorPrimitive.Root>
    <ErrorPrimitive.Message />
  </ErrorPrimitive.Root>
);
